'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import i18n from '@/shared/i18n';

// Synchronously set language BEFORE React renders
// Default locale from runtime config: 'ru' for darpiano.ru, 'en' for darpiano.com
if (typeof window !== 'undefined') {
  const pathname = window.location.pathname;
  const defaultLocale = window.__RUNTIME_CONFIG__?.defaultLocale || 'en';
  let initialLang = defaultLocale;

  // Check Russian routes FIRST (before deployment default)
  if (pathname === '/ru' || pathname.startsWith('/ru/') || pathname === '/posts/ru' || pathname.startsWith('/posts/ru/')) {
    initialLang = 'ru';
  } else if (pathname.startsWith('/admin') || pathname.startsWith('/debug')) {
    // For /admin and /debug, preserve current i18n language
    initialLang = i18n.language || defaultLocale;
  }
  // Otherwise: use deployment default (covers /, /practice, /posts, /about, etc.)

  // Set language immediately if it's not already correct
  if (i18n.language !== initialLang) {
    i18n.changeLanguage(initialLang);
  }
  document.documentElement.lang = initialLang;
}

export default function I18nProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    // Initialize i18n on the client side
    if (typeof window !== 'undefined') {
      window.i18n = i18n;

      // Listen for i18next language changes and update HTML lang attribute
      const handleLanguageChange = (lng: string) => {
        document.documentElement.lang = lng;
      };

      i18n.on('languageChanged', handleLanguageChange);

      return () => {
        i18n.off('languageChanged', handleLanguageChange);
      };
    }
  }, []);

  useEffect(() => {
    // Sync language with URL path on route changes (for client-side navigation)
    if (typeof window !== 'undefined') {
      const defaultLocale = window.__RUNTIME_CONFIG__?.defaultLocale || 'en';

      // Check for Russian routes FIRST (before deployment default)
      if (pathname === '/ru' || pathname.startsWith('/ru/') || pathname === '/posts/ru' || pathname.startsWith('/posts/ru/')) {
        const detectedLang = 'ru';
        document.documentElement.lang = detectedLang;
        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } else if (pathname.startsWith('/admin') || pathname.startsWith('/debug')) {
        // For /admin and /debug routes, preserve the current language
        const currentLang = i18n.language || document.documentElement.lang;
        if (document.documentElement.lang !== currentLang) {
          document.documentElement.lang = currentLang;
        }
      } else {
        // All other routes (/, /practice, /posts, /about, etc.) use deployment default
        document.documentElement.lang = defaultLocale;
        if (i18n.language !== defaultLocale) {
          i18n.changeLanguage(defaultLocale);
        }
      }
    }
  }, [pathname]);

  return <>{children}</>;
} 